import { TRAILING_SLASH_ENABLED } from './trailingSlash';
// Imported from the leaf module (not the `useAppRouter` barrel) to avoid a
// circular import: `useAppRouter` already imports this `routerUtils` package.
import { getAppBasename } from '../../hooks/useAppRouter/getAppBasename';

/**
 * Frontegg renders its auth UI (login, sign-up, reset-password, …) by matching
 * the browser URL against `defaultFronteggRoutes`, which on deployed builds are
 * trailing-slash paths. The static export no longer emits per-route HTML for
 * those URLs, so the edge's directory-style `/path` → `/path/` redirect can no
 * longer fire and a slashless entry URL (e.g. `/v2/auth/account/login`) lands
 * on the catch-all shell where Frontegg finds no match and renders nothing.
 *
 * `useAppRouter` already normalizes client-side navigations; this covers the
 * remaining case — a full-page load that enters directly on a slashless auth
 * URL — by redirecting to the trailing-slash form before the router and
 * `FronteggProvider` read the URL. A `/auth/` prefix match (rather than the
 * exact route list) also covers nested callback URLs (magic-link, OAuth, SAML)
 * and preserves their query string and hash.
 */
export const canonicalizeAuthTrailingSlash = (): void => {
  if (!TRAILING_SLASH_ENABLED) {
    return;
  }

  const { pathname, search, hash } = window.location;
  const authPrefix = `${getAppBasename()}/auth/`;

  if (!pathname.startsWith(authPrefix) || pathname.endsWith('/')) {
    return;
  }

  window.location.replace(`${pathname}/${search}${hash}`);
};
